import { keepPreviousData, useQuery } from '@tanstack/react-query';

import httpInstances from '@/api/axiosInstance';
import type { MarketApps, WidgetV2 } from '@/features/Dashboard/types';
import type { Market, MarketDto } from '@/types/markets';
import { detectEnvironment } from '@/utils/detectEnvironment';
import { parseMarketComponent } from '@/utils/parseMarketComponent';
import { sanitizeName } from '@/utils/sanitizeName';

export const useMarketQuery = (marketId: string | undefined) => {
  return useQuery({
    queryKey: ['market', marketId],
    queryFn: async (): Promise<Market> => {
      const { data } = await httpInstances.axiosInternalInstance.get<MarketDto>(
        `${detectEnvironment() === 'staging' ? '/staging/' : '/'}markets/${marketId}`
      );

      const pages = data.pages.map(page => ({
        ...page,
        id: sanitizeName(page.name),
        components: page.components.map(component => {
          return parseMarketComponent(component, data?.version);
        })
      }));

      return {
        ...data,
        id: marketId!,
        pages
      };
    },
    enabled: !!marketId,
    refetchOnWindowFocus: true,
    staleTime: 5 * 60 * 1000
  });
};

export type WidgetItem = {
  name: string;
  type: string;
  definition: WidgetV2;
};

export interface MarketTabWidgets {
  name: string;
  widgets: WidgetItem[];
}

export const useMarketWidgets = (marketId: string | null) => {
  return useQuery({
    queryKey: ['marketWidgets', marketId],
    queryFn: async () => {
      const { data } = await httpInstances.axiosInternalInstance.get<any[]>(
        `${detectEnvironment() === 'staging' ? '/staging/' : '/'}markets/${marketId}/widgets`
      );
      return data.map(tab => {
        const widgets: WidgetV2[] = [];
        for (const widget of tab.widgets) {
          if (widget.definition.tabs) {
            widget.definition.tabs.map((tab: any) => {
              widgets.push({
                name: tab.name,
                type: widget.type,
                definition: { ...tab, layout: widget.definition?.layout, type: widget.type }
              } as unknown as WidgetV2);
            });
          } else {
            widgets.push(widget as unknown as WidgetV2);
          }
        }
        return { ...tab, widgets };
      }) as unknown as MarketTabWidgets[];
    },
    enabled: !!marketId,
    refetchOnWindowFocus: true,
    staleTime: 5 * 60 * 1000
  });
};

export const useMarketPreview = (template: MarketApps | undefined, enabled = true) => {
  return useQuery({
    placeholderData: keepPreviousData,
    queryKey: ['marketPreview', template ? JSON.stringify(template) : null, template],
    queryFn: async () => {
      const { data } = await httpInstances.axiosInternalInstance.post<MarketDto>(
        `${detectEnvironment() === 'staging' ? '/staging/' : '/'}markets/preview`,
        {
          version: 2,
          pages: [
            {
              name: '',
              components: template
            }
          ]
        }
      );

      const pages = data.pages.map(page => ({
        ...page,
        id: sanitizeName(page.name),
        components: page.components.map((component: any, componentIndex) => {
          if (!template) return component;
          const parsedComponent = parseMarketComponent(component, '2');
          if (parsedComponent.component?.type === 'tiles') {
            return {
              ...parsedComponent,
              component: {
                ...parsedComponent.component,
                tiles: parsedComponent.component.tiles.map((tile, index) => {
                  const temp = template[0] as any;
                  return {
                    ...tile,
                    id: temp.tiles[index].id
                  };
                })
              }
            };
          }
          return {
            ...parsedComponent,
            id: template[componentIndex].id,
            tabs: parsedComponent.tabs?.map((tab, index) => {
              const temp = template[componentIndex] as any;
              return {
                ...tab,
                id: temp.tabs[index].id
              };
            })
          };
        })
      }));

      if (pages.length === 0) {
        return null;
      }

      return pages[0];
    },
    enabled: !!template && template.length > 0 && enabled,
    refetchOnWindowFocus: true,
    staleTime: 5 * 60 * 1000
  });
};
